import { motion } from 'framer-motion';
import { isFunction } from 'lodash';
import { CheckCircle2, ChevronRight, Container, PackageIcon } from 'lucide-react';
import pluralize from 'pluralize';

import { Icon } from '@/components/icon';
import { Tooltip } from '@/components/tooltip';
import { MODE_AIR, MODE_INTERMODAL, MODE_PARCEL } from '@/lib/constants';
import { ShipmentModel } from '@/lib/models/shipment/types';
import { getTransportColor } from '@/utils/helpers/get-transport-color';
import { tv } from '@/utils/styles';

export interface ShipmentIconProps {
  type: ShipmentModel['type'];
  isCompleted?: boolean;
  count?: number;
  canExpand?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const ButtonWrap = ({
  isButton,
  count,
  children,
  className,
  onClick,
}: {
  isButton?: boolean;
  count: number;
  children: React.ReactNode;
  className: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) =>
  isButton ? (
    <Tooltip content={`Expand to see ${count} ${pluralize('container', count)}`} placement="right" trigger={['hover']}>
      <button type="button" className={className} onClick={onClick}>
        {children}
      </button>
    </Tooltip>
  ) : (
    <div className={className}>{children}</div>
  );

const ShipmentIcon = ({ type, isCompleted, count = 0, canExpand, isExpanded, onToggleExpand }: ShipmentIconProps) => {
  const shipmentType = canExpand ? 'group' : type;

  const handleOnToggle = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (isFunction(onToggleExpand)) onToggleExpand(e);
  };

  const { base, trigger, wrapper, statusIcon, containerCount, indicator } = styles({
    type,
    isCompleted,
    hasCount: canExpand,
  });

  return (
    <div className={base()}>
      <ButtonWrap count={count} isButton={canExpand} className={canExpand ? trigger() : ''} onClick={handleOnToggle}>
        <div className="relative">
          {isCompleted && (
            <div className={statusIcon()}>
              <CheckCircle2 />
            </div>
          )}
          <div className={wrapper()}>
            {shipmentType === MODE_AIR && <Icon color={getTransportColor(MODE_AIR)} size="16px" name="plane" />}
            {shipmentType === MODE_INTERMODAL && <Container className="size-4 text-lightBlue-600" />}
            {shipmentType === MODE_PARCEL && <PackageIcon className="size-4 text-lightBlue-600" />}
            {shipmentType === 'group' && <span className={containerCount()}>{count}</span>}
          </div>
          {canExpand && (
            <motion.div
              initial={{ rotate: '0deg' }}
              animate={isExpanded ? 'expanded' : 'collapsed'}
              variants={{ expanded: { rotate: '90deg' } }}
              className={indicator()}
            >
              <ChevronRight size={18} />
            </motion.div>
          )}
        </div>
      </ButtonWrap>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative grid h-6 place-items-center pl-2',
    trigger: 'group -m-2 -ml-4 p-2 pl-4',
    wrapper: 'flex h-6 min-w-6 items-center justify-center rounded-full',
    statusIcon:
      'absolute -right-1 -top-1 z-2 grid size-3.5 place-items-center rounded-full bg-white p-px text-green-600 [&_svg]:size-full',
    containerCount: 'text-xs font-bold text-lightBlue-600',
    indicator: 'absolute -left-[18px] top-1 size-4 text-grey-350 group-hover:text-lightBlue-600 [&_svg]:size-4',
  },
  variants: {
    type: {
      air: {
        wrapper: 'bg-transport-plane/20',
      },
      intermodal: {
        wrapper: 'bg-transport-ship/20',
      },
      parcel: {
        wrapper: 'bg-transport-ship/20',
      },
    },
    isCompleted: {
      true: {
        wrapper: 'grayscale group-hover:grayscale-0',
      },
      false: {},
    },
    hasCount: {
      true: {
        wrapper: 'border border-dotted border-lightBlue-600 px-0.5',
      },
      false: {},
    },
  },
});

export { ShipmentIcon };
